import type React from "react"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from "react"

import type {
  ConversationEvent,
  StageTransitionPayload,
} from "@/types/conversation"
import type { Subscription } from "@/utils/cable"
import { Cable } from "@/utils/cable"

// Types
export interface TimelineEntry {
  event_id: string
  cursor: string
  type: string
  timestamp: number
  relative_time: number
  stage: string
  label: string
  metadata?: Record<string, unknown>
}

export interface StageInfo {
  stage: string
  cursor: string
  timestamp: number
  reason?: string
}

export interface InitialState {
  stage: string
}

export interface ReplayEventMetadata {
  relative_time: number
}

/** ConversationEvent extended with replay-specific metadata from the backend */
export type ReplayConversationEvent = ConversationEvent & {
  replay_metadata?: ReplayEventMetadata
}

export interface PlaybackInfo {
  current_index?: number
  elapsed_ms?: number
}

export interface PlaybackUpdate {
  current_index?: number
  elapsed_ms?: number
}

/** WebSocket message types from ReplayChannel */
export type ReplayWebSocketMessage =
  | {
      type: "replay_event"
      event: ReplayConversationEvent
      playback_info?: PlaybackInfo
    }
  | { type: "playback_started" }
  | { type: "playback_paused" }
  | { type: "seek_complete"; events_to_rebuild?: ReplayConversationEvent[] }
  | { type: "replay_complete" }
  | { type: "replay_error"; error: string }
  | { type: "speed_changed"; speed: number }

export interface ReplayMetadata {
  conversation_id: string
  session_token: string
  total_events: number
  duration_ms: number
  stages: StageInfo[]
  timeline: TimelineEntry[]
  initial_state: InitialState
  first_event_cursor: string
  last_event_cursor: string
}

export interface ReplayState {
  conversationId: number | null
  metadata: ReplayMetadata | null
  events: ReplayConversationEvent[]
  currentEventIndex: number
  isPlaying: boolean
  playbackSpeed: number
  elapsed: number
  currentStage: string
  bufferedEvents: ReplayConversationEvent[]
  isLoading: boolean
  error: string | null
  wsConnected: boolean
}

type ReplayAction =
  | {
      type: "INIT_REPLAY"
      payload: {
        conversationId: number
        metadata: ReplayMetadata
        events: ReplayConversationEvent[]
      }
    }
  | {
      type: "RECEIVE_EVENT"
      payload: { event: ReplayConversationEvent; playback_info?: PlaybackInfo }
    }
  | { type: "UPDATE_PLAYBACK"; payload: PlaybackUpdate }
  | { type: "PLAY" }
  | { type: "PAUSE" }
  | { type: "SEEK"; payload: { position: number; eventIndex: number } }
  | { type: "SET_SPEED"; payload: number }
  | { type: "SET_EVENTS"; payload: ReplayConversationEvent[] }
  | { type: "SET_ERROR"; payload: string }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "WS_CONNECTED"; payload: boolean }
  | { type: "REPLAY_COMPLETE" }
  | { type: "RESET" }

const initialState: ReplayState = {
  conversationId: null,
  metadata: null,
  events: [],
  currentEventIndex: 0,
  isPlaying: false,
  playbackSpeed: 1,
  elapsed: 0,
  currentStage: "bootstrapping",
  bufferedEvents: [],
  isLoading: false,
  error: null,
  wsConnected: false,
}

function replayReducer(state: ReplayState, action: ReplayAction): ReplayState {
  switch (action.type) {
    case "INIT_REPLAY":
      return {
        ...state,
        conversationId: action.payload.conversationId,
        metadata: action.payload.metadata,
        events: action.payload.events,
        currentStage: action.payload.metadata.initial_state.stage,
        isLoading: false,
        error: null,
      }

    case "RECEIVE_EVENT": {
      const incoming = action.payload.event
      // If this event already exists in our local list (e.g., preloaded), move the index to it
      const existingIdx = state.events.findIndex(
        (e) => e.event_id === incoming.event_id,
      )
      let events = state.events
      let nextIndex = state.currentEventIndex

      if (existingIdx !== -1) {
        nextIndex = existingIdx
      } else {
        events = [...state.events, incoming]
        nextIndex = state.isPlaying
          ? Math.min(events.length - 1, state.currentEventIndex + 1)
          : state.currentEventIndex
      }

      // Update stage if this is a stage transition event
      let newStage = state.currentStage
      if (incoming.type === "stage_transition") {
        const payload = incoming.payload as StageTransitionPayload
        newStage = payload.to_stage ?? state.currentStage
      }

      return {
        ...state,
        events,
        currentEventIndex: nextIndex,
        currentStage: newStage,
        elapsed: incoming.replay_metadata?.relative_time ?? state.elapsed,
      }
    }

    case "UPDATE_PLAYBACK":
      return {
        ...state,
        currentEventIndex:
          typeof action.payload.current_index === "number"
            ? action.payload.current_index
            : state.currentEventIndex,
        elapsed:
          typeof action.payload.elapsed_ms === "number"
            ? action.payload.elapsed_ms
            : state.elapsed,
      }

    case "PLAY":
      return { ...state, isPlaying: true }

    case "PAUSE":
      return { ...state, isPlaying: false }

    case "SEEK":
      return {
        ...state,
        elapsed: action.payload.position,
        currentEventIndex: action.payload.eventIndex,
      }

    case "SET_SPEED":
      return { ...state, playbackSpeed: action.payload }

    case "SET_EVENTS":
      return { ...state, events: action.payload }

    case "SET_ERROR":
      return { ...state, error: action.payload, isLoading: false }

    case "SET_LOADING":
      return { ...state, isLoading: action.payload }

    case "WS_CONNECTED":
      return { ...state, wsConnected: action.payload }

    case "REPLAY_COMPLETE":
      return { ...state, isPlaying: false }

    case "RESET":
      return initialState

    default:
      return state
  }
}

// Context
interface ReplayContextValue extends ReplayState {
  play: () => void
  pause: () => void
  seek: (position: number) => void
  setSpeed: (speed: number) => void
  jumpToEvent: (eventId: string) => void
  jumpToCursor: (cursor: string) => void
  reset: () => void
}

const ReplayContext = createContext<ReplayContextValue | null>(null)

export function useReplay() {
  const context = useContext(ReplayContext)
  if (!context) {
    throw new Error("useReplay must be used within ReplayProvider")
  }
  return context
}

// Provider Component
interface ReplayProviderProps {
  conversationId: number
  replayMetadata?: ReplayMetadata
  initialEvents?: ReplayConversationEvent[]
  wsUrl?: string
  children: React.ReactNode
}

export function ReplayProvider({
  conversationId,
  replayMetadata,
  initialEvents = [],
  wsUrl = "/cable",
  children,
}: ReplayProviderProps) {
  const [state, dispatch] = useReducer(replayReducer, initialState)
  const cableRef = useRef<Cable | null>(null)
  const channelRef = useRef<Subscription | null>(null)
  const rafRef = useRef<number | null>(null)
  const lastTsRef = useRef<number>(0)
  const elapsedRef = useRef<number>(0)
  const speedRef = useRef<number>(1)
  const eventsRef = useRef<ReplayConversationEvent[]>([])
  const durationRef = useRef<number>(0)

  // Initialize with props from Inertia
  useEffect(() => {
    if (replayMetadata && conversationId) {
      dispatch({
        type: "INIT_REPLAY",
        payload: {
          conversationId,
          metadata: replayMetadata,
          events: initialEvents,
        },
      })

      // Setup WebSocket for streaming additional events
      setupWebSocket()
    }

    return () => cleanup()
  }, [conversationId])

  // Keep refs in sync with state for animation loop
  useEffect(() => {
    elapsedRef.current = state.elapsed
  }, [state.elapsed])
  useEffect(() => {
    speedRef.current = state.playbackSpeed
  }, [state.playbackSpeed])
  useEffect(() => {
    eventsRef.current = state.events
  }, [state.events])
  useEffect(() => {
    durationRef.current = state.metadata?.duration_ms ?? 0
  }, [state.metadata])

  const setupWebSocket = () => {
    const cable = new Cable(wsUrl, { reconnect: true })
    cableRef.current = cable

    channelRef.current = cable.subscribe(
      "ReplayChannel",
      { conversation_id: conversationId },
      {
        connected: () => {
          console.log("Replay channel connected")
          dispatch({ type: "WS_CONNECTED", payload: true })
        },
        disconnected: () => {
          dispatch({ type: "WS_CONNECTED", payload: false })
        },
        received: (data: unknown) => {
          handleWebSocketMessage(data as ReplayWebSocketMessage)
        },
      },
    )

    cable.open()
  }

  const handleWebSocketMessage = (message: ReplayWebSocketMessage) => {
    switch (message.type) {
      case "replay_event":
        dispatch({
          type: "RECEIVE_EVENT",
          payload: {
            event: message.event,
            playback_info: message.playback_info,
          },
        })
        break
      case "playback_started":
        dispatch({ type: "PLAY" })
        break
      case "playback_paused":
        dispatch({ type: "PAUSE" })
        break
      case "seek_complete":
        if (message.events_to_rebuild) {
          dispatch({ type: "SET_EVENTS", payload: message.events_to_rebuild })
        }
        break
      case "replay_complete":
        dispatch({ type: "REPLAY_COMPLETE" })
        break
      case "replay_error":
        dispatch({ type: "SET_ERROR", payload: message.error })
        break
      case "speed_changed":
        dispatch({ type: "SET_SPEED", payload: message.speed })
        break
    }
  }

  const cleanup = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    if (channelRef.current) {
      try {
        channelRef.current.unsubscribe()
      } catch (e) {
        console.error("Error unsubscribing:", e)
      }
      channelRef.current = null
    }
    if (cableRef.current) {
      cableRef.current = null
    }
    dispatch({ type: "WS_CONNECTED", payload: false })
  }

  // Control functions
  const play = useCallback(() => {
    // Start local playback immediately
    dispatch({ type: "PLAY" })
    // Notify server (best-effort)
    channelRef.current?.perform("play", {
      cursor: state.events[state.currentEventIndex]?.cursor || "000001",
      speed: state.playbackSpeed,
    })
  }, [state.currentEventIndex, state.playbackSpeed, state.events])

  const pause = useCallback(() => {
    dispatch({ type: "PAUSE" })
    channelRef.current?.perform("pause")
  }, [])

  const seek = useCallback(
    (position: number) => {
      if (!state.metadata) return

      const targetTime = (position / 100) * state.metadata.duration_ms
      const eventIndex = findEventAtTime(state.events, targetTime)

      channelRef.current?.perform("seek", {
        target: { type: "time", value: targetTime },
        speed: state.playbackSpeed,
      })

      dispatch({ type: "SEEK", payload: { position: targetTime, eventIndex } })
    },
    [state.events, state.metadata, state.playbackSpeed],
  )

  const setSpeed = useCallback((speed: number) => {
    channelRef.current?.perform("set_speed", { speed })
    dispatch({ type: "SET_SPEED", payload: speed })
  }, [])

  const jumpToEvent = useCallback(
    (eventId: string) => {
      const index = state.events.findIndex((e) => e.event_id === eventId)
      if (index !== -1) {
        const event = state.events[index]
        const relativeTime = event.replay_metadata?.relative_time ?? 0
        channelRef.current?.perform("seek", {
          target: { type: "cursor", value: event.cursor },
          speed: state.playbackSpeed,
        })
        dispatch({
          type: "SEEK",
          payload: { position: relativeTime, eventIndex: index },
        })
      }
    },
    [state.events, state.playbackSpeed],
  )

  const jumpToCursor = useCallback(
    (cursor: string) => {
      const index = state.events.findIndex((e) => e.cursor === cursor)
      if (index !== -1) {
        const event = state.events[index]
        const relativeTime = event.replay_metadata?.relative_time ?? 0
        channelRef.current?.perform("seek", {
          target: { type: "cursor", value: cursor },
          speed: state.playbackSpeed,
        })
        dispatch({
          type: "SEEK",
          payload: { position: relativeTime, eventIndex: index },
        })
      }
    },
    [state.events, state.playbackSpeed],
  )

  const reset = useCallback(() => {
    pause()
    dispatch({ type: "RESET" })
  }, [pause])

  // Animation loop for realtime playback progression
  useEffect(() => {
    if (!state.isPlaying || !state.metadata) return

    lastTsRef.current = performance.now()

    const tick = () => {
      const now = performance.now()
      const dt = now - lastTsRef.current
      lastTsRef.current = now

      const scaled = dt * (speedRef.current || 1)
      let nextElapsed = elapsedRef.current + scaled
      const duration = durationRef.current

      if (duration > 0 && nextElapsed >= duration) {
        nextElapsed = duration
      }

      const idx = findEventAtTime(eventsRef.current, nextElapsed)

      dispatch({
        type: "UPDATE_PLAYBACK",
        payload: { elapsed_ms: nextElapsed, current_index: idx },
      })

      if (duration > 0 && nextElapsed >= duration) {
        dispatch({ type: "REPLAY_COMPLETE" })
        rafRef.current = null
        return
      }

      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [state.isPlaying, state.metadata])

  const value: ReplayContextValue = {
    ...state,
    play,
    pause,
    seek,
    setSpeed,
    jumpToEvent,
    jumpToCursor,
    reset,
  }

  return (
    <ReplayContext.Provider value={value}>{children}</ReplayContext.Provider>
  )
}

// Helper functions
function findEventAtTime(
  events: ReplayConversationEvent[],
  targetTime: number,
): number {
  if (events.length === 0) return 0

  // Find the event closest to the target time
  for (let i = 0; i < events.length; i++) {
    const event = events[i]
    const relativeTime = event.replay_metadata?.relative_time ?? 0
    if (relativeTime >= targetTime) {
      return i
    }
  }

  return events.length - 1
}
